import React, { useEffect, useState } from 'react';
import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteClient, { DytePlugin } from '@dytesdk/web-core';
import DyteHighlightedGrid from './DyteHighligtedGrid';
import { IconPack, defaultIconPack } from '../lib/icons';
import DyteSpotlightGrid from './DyteSpotlightGrid';
import { DyteI18n, useLanguage } from '../lib/lang';
import { UIConfig } from '../types/ui-config';
import { States } from '../types/props';
import { defaultConfig } from '../lib/default-ui-config';
import { Peer } from '../types/dyte-client';
import DyteSimpleGrid from './DyteSimpleGrid';

export default function DyteMixedGrid({
  aspectRatio = '16:9',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  gap = 8,
  meeting,
  participants = [],
  pinnedParticipants = [],
  screenShareParticipants = [],
  plugins = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variant = 'solid',
  iconPack = defaultIconPack,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  states,
  t,
}: {
  aspectRatio?: string;
  config?: UIConfig;
  gap?: number;
  meeting: DyteClient;
  participants: Peer[];
  pinnedParticipants: Peer[];
  screenShareParticipants: Peer[];
  plugins: DytePlugin[];
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'boxed' | 'solid';
  iconPack?: IconPack;
  states?: States;
  t?: DyteI18n;
}) {
  const [partipantList, setParticipantList] = useState(participants);
  const [pinnedParticipantsList, setPinnedParticipantsList] =
    useState(pinnedParticipants);
  const [pluginList, setPluginList] = useState(plugins);
  const [screenShareList, setScreenShareList] = useState(
    screenShareParticipants
  );
  useEffect(() => {
    setParticipantList(participants);
    setPinnedParticipantsList(pinnedParticipants);
    setPluginList(plugins);
    setScreenShareList(screenShareParticipants);
  }, [participants, pinnedParticipants, plugins, screenShareParticipants]);
  const sharingScreen = useDyteSelector((m) => m.self.screenShareEnabled);
  const stageStatus = useDyteSelector((m) => m.stage.status);
  const activePlugins = pluginList;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const allowHighlighedGrid = () => {
    if (
      meeting.meta.viewType === 'GROUP_CALL' ||
      meeting.meta.viewType === 'WEBINAR'
    )
      return true;
    if (meeting.meta.viewType === 'LIVESTREAM' && stageStatus === 'ON_STAGE')
      return true;
    return false;
  };
  if (
    allowHighlighedGrid() &&
    participants.filter((p) => p.screenShareEnabled).length === 0 &&
    !sharingScreen &&
    activePlugins.length === 0 &&
    pinnedParticipants.length > 0
  )
    return (
      <DyteSpotlightGrid
        meeting={meeting}
        participants={partipantList}
        pinnedParticipants={pinnedParticipantsList}
        aspectRatio={aspectRatio}
        iconPack={iconPack}
        gap={gap}
        t={t}
      />
    );

  // Screenshare & Plugin Grid
  if (
    allowHighlighedGrid() &&
    (partipantList.filter((p) => p.screenShareEnabled).length > 0 ||
      sharingScreen ||
      activePlugins.length > 0 ||
      pinnedParticipantsList.length > 0)
  ) {
    return (
      <DyteHighlightedGrid
        meeting={meeting}
        participants={partipantList}
        pinnedParticipants={pinnedParticipantsList}
        screenshareParticipants={screenShareList}
        plugins={pluginList}
        iconPack={iconPack}
      />
    );
  }

  return (
    <DyteSimpleGrid
      meeting={meeting}
      participants={[...partipantList]}
      aspectRatio={aspectRatio}
      iconPack={iconPack}
      gap={gap}
      t={t}
    />
  );
}
